import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

import { MediaFile } from '../../models';

const CACHE_TTL_MS = 30 * 60 * 1000;
const IMAGE_CACHE_MAX = 12;
const HEIC_CACHE_MAX = 5;
const AUDIO_CACHE_MAX = 5;

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
  lastAccessedAt: number;
}

export interface AudioWaveformCache {
  peaks: number[][];
  duration: number;
}

interface AudioSessionCache {
  blob?: Blob;
  waveform?: AudioWaveformCache;
}

export interface CachedMessageMedia {
  image?: MediaFile[];
  audio?: MediaFile[];
  video?: MediaFile[];
  application?: MediaFile[];
}

@Injectable({
  providedIn: 'root',
})
export class MediaSessionCacheService {
  private imageCache = new Map<string, CacheEntry<true>>();
  private heicCache = new Map<string, CacheEntry<Blob>>();
  private audioCache = new Map<string, CacheEntry<AudioSessionCache>>();
  private messageMediaCache = new Map<string, CacheEntry<CachedMessageMedia>>();

  private heicInFlight = new Map<string, Observable<Blob>>();
  private audioInFlight = new Map<string, Observable<Blob>>();

  buildMessageMediaKey(links: string[]): string {
    return links.map((link) => this.normalizeKey(link)).join('||');
  }

  isImageLoaded(src: string): boolean {
    return !!this.getEntry(this.imageCache, this.normalizeKey(src));
  }

  markImageLoaded(src: string): void {
    this.setEntry(this.imageCache, this.normalizeKey(src), true, IMAGE_CACHE_MAX);
  }

  getCachedHeicBlob(key: string): Blob | null {
    return this.getEntry(this.heicCache, this.normalizeKey(key)) ?? null;
  }

  getOrCreateHeicBlob(key: string, factory: () => Observable<Blob>): Observable<Blob> {
    const normalizedKey = this.normalizeKey(key);
    const cachedBlob = this.getEntry(this.heicCache, normalizedKey);
    if (cachedBlob) {
      return of(cachedBlob);
    }

    const inFlight = this.heicInFlight.get(normalizedKey);
    if (inFlight) {
      return inFlight;
    }

    const request$ = factory().pipe(
      tap((blob) => {
        this.setEntry(this.heicCache, normalizedKey, blob, HEIC_CACHE_MAX);
      }),
      finalize(() => {
        this.heicInFlight.delete(normalizedKey);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.heicInFlight.set(normalizedKey, request$);
    return request$;
  }

  getCachedAudio(url: string): AudioSessionCache | null {
    return this.getEntry(this.audioCache, this.normalizeKey(url)) ?? null;
  }

  getCachedMessageMedia(key: string): CachedMessageMedia | null {
    return this.getEntry(this.messageMediaCache, key) ?? null;
  }

  setCachedMessageMedia(key: string, media: CachedMessageMedia): void {
    this.setEntry(this.messageMediaCache, key, media, IMAGE_CACHE_MAX);
  }

  getOrCreateAudioBlob(url: string, factory: () => Observable<Blob>): Observable<Blob> {
    const normalizedUrl = this.normalizeKey(url);
    const cachedAudio = this.getEntry(this.audioCache, normalizedUrl);
    if (cachedAudio?.blob) {
      return of(cachedAudio.blob);
    }

    const inFlight = this.audioInFlight.get(normalizedUrl);
    if (inFlight) {
      return inFlight;
    }

    const request$ = factory().pipe(
      tap((blob) => {
        const current = this.getEntry(this.audioCache, normalizedUrl) ?? {};
        this.setEntry(this.audioCache, normalizedUrl, { ...current, blob }, AUDIO_CACHE_MAX);
      }),
      finalize(() => {
        this.audioInFlight.delete(normalizedUrl);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.audioInFlight.set(normalizedUrl, request$);
    return request$;
  }

  setAudioWaveform(url: string, waveform: AudioWaveformCache): void {
    const normalizedUrl = this.normalizeKey(url);
    const current = this.getEntry(this.audioCache, normalizedUrl) ?? {};
    this.setEntry(this.audioCache, normalizedUrl, { ...current, waveform }, AUDIO_CACHE_MAX);
  }

  private getEntry<T>(cache: Map<string, CacheEntry<T>>, key: string): T | undefined {
    this.removeExpiredEntries(cache);

    const entry = cache.get(key);
    if (!entry) {
      return undefined;
    }

    entry.lastAccessedAt = Date.now();
    entry.expiresAt = Date.now() + CACHE_TTL_MS;
    return entry.value;
  }

  private setEntry<T>(cache: Map<string, CacheEntry<T>>, key: string, value: T, maxEntries: number): void {
    this.removeExpiredEntries(cache);

    cache.set(key, {
      value,
      expiresAt: Date.now() + CACHE_TTL_MS,
      lastAccessedAt: Date.now(),
    });

    while (cache.size > maxEntries) {
      const oldestKey = this.getOldestKey(cache);
      if (!oldestKey) {
        break;
      }

      cache.delete(oldestKey);
    }
  }

  private removeExpiredEntries<T>(cache: Map<string, CacheEntry<T>>): void {
    const now = Date.now();
    for (const [key, entry] of cache.entries()) {
      if (entry.expiresAt <= now) {
        cache.delete(key);
      }
    }
  }

  private getOldestKey<T>(cache: Map<string, CacheEntry<T>>): string | undefined {
    let oldestKey: string | undefined;
    let oldestAccess = Number.POSITIVE_INFINITY;

    for (const [key, entry] of cache.entries()) {
      if (entry.lastAccessedAt < oldestAccess) {
        oldestAccess = entry.lastAccessedAt;
        oldestKey = key;
      }
    }

    return oldestKey;
  }

  private normalizeKey(key: string): string {
    if (key.startsWith('data:')) {
      return key;
    }

    try {
      const url = new URL(key);
      return `${url.origin}${url.pathname}`;
    } catch {
      return key.split('?')[0];
    }
  }
}
